// 모바일 시세 그래프 — 차수별현황에서 종목 탭 시 진입
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { fetchStockChart } from '../api/mobileApi';
import '../styles/mobile.css';

// ── 차수 색상 (4개 순환) ──────────────────────────────────
const TURN_COLORS = ['#e34948', '#e87ba4', '#eda100', '#1baf7a'];
const turnColor = n => TURN_COLORS[(n - 1) % 4];

// ── 포맷 헬퍼 ─────────────────────────────────────────────
const fRate = v => {
  if (v == null) return '';
  const n = Number(v);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};
const fPrice = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString('ko-KR');
};
const fQty = v => {
  if (v == null) return '';
  const n = Number(v);
  return n % 1 !== 0
    ? n.toLocaleString('ko-KR', { maximumFractionDigits: 4 })
    : n.toLocaleString('ko-KR');
};
const fDate = d => {
  if (!d) return '';
  return `${d.substring(2, 4)}.${d.substring(5, 7)}.${d.substring(8, 10)}`;
};

// ── 기간 정의 ─────────────────────────────────────────────
const PERIODS = [
  { value: '1m', label: '1개월' },
  { value: '3m', label: '3개월' },
  { value: '6m', label: '6개월' },
  { value: '1y', label: '1년' },
  { value: '3y', label: '3년' },
  { value: '5y', label: '5년' },
];

// XAxis 틱 간격 (거래일 기준 근사)
const PERIOD_INTERVAL = {
  '1m':  4,
  '3m': 13,
  '6m': 25,
  '1y': 50,
  '3y': 130,
  '5y': 260,
};

const formatTick = (dateStr, period) => {
  if (!dateStr) return '';
  const m = dateStr.substring(5, 7);
  const d = dateStr.substring(8, 10);
  const yy = dateStr.substring(2, 4);
  if (period === '1m' || period === '3m') return `${parseInt(m)}/${parseInt(d)}`;
  if (period === '6m' || period === '1y') return `${yy}.${m}`;
  return `${dateStr.substring(0, 4)}.${m}`;
};

// 매수일이 데이터 없는 날짜일 때 가장 가까운 날짜 찾기
const findClosestDate = (dateStr, dateList) => {
  if (!dateList?.length || !dateStr) return null;
  const target = new Date(dateStr).getTime();
  return dateList.reduce((best, d) => {
    const bestDiff = Math.abs(new Date(best).getTime() - target);
    const currDiff = Math.abs(new Date(d).getTime() - target);
    return currDiff < bestDiff ? d : best;
  }, dateList[0]);
};

// Y축 간결 표기
const fAxisPrice = v => {
  if (v == null) return '';
  const n = Number(v);
  if (n >= 100000) return `${Math.round(n / 10000)}만`;
  if (n >= 10000)  return `${(n / 10000).toFixed(1)}만`;
  return n.toLocaleString('ko-KR');
};

// ── 수평 기준선 라벨 (매입단가) ───────────────────────────
const HRefLabel = ({ viewBox, color, caption }) => {
  if (!viewBox) return null;
  const { x, y, width } = viewBox;
  return (
    <text
      x={x + width - 4}
      y={y - 4}
      textAnchor="end"
      fontSize={9}
      fill={color}
      fontWeight="700"
    >
      {caption}
    </text>
  );
};

// ── 수직 기준선 라벨 (매수일) ─────────────────────────────
const VRefLabel = ({ viewBox, color, caption }) => {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  return (
    <text
      x={x + 3}
      y={y + height - 5}
      textAnchor="start"
      fontSize={9}
      fill={color}
      fontWeight="700"
    >
      {caption}
    </text>
  );
};

// ── 커스텀 툴팁 ───────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, turns }) => {
  if (!active || !payload?.length) return null;
  const price = payload[0]?.value;
  if (price == null) return null;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e4e8f0',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 11,
      minWidth: 110,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 5, color: '#555' }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 3 }}>
        <span style={{ color: '#888' }}>시세</span>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{fPrice(price)}</span>
      </div>
      {turns?.filter(t => t.buy_price != null).map(t => {
        const rate = price > 0 && t.buy_price > 0
          ? (price - t.buy_price) / t.buy_price * 100
          : null;
        const color = turnColor(t.turn_no);
        const cls = rate == null ? '' : rate >= 0 ? '#e53935' : '#1565c0';
        return (
          <div key={t.turn_no} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ color }}>{t.turn_no}차</span>
            <span style={{ color: cls, fontWeight: 600 }}>
              {rate != null ? fRate(rate) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
export default function StockChartPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const state     = location.state;

  const [period,    setPeriod]    = useState('3m');
  const [chartData, setChartData] = useState([]);
  const [loading,   setLoading]   = useState(false);

  const hasState = !!(state?.ticker);
  const {
    ticker, stock_name, account_name, term_type,
    turns = [], current_price, diff_rate,
  } = state || {};

  const unsoldTurns = turns.filter(t => t.buy_price != null);

  // ── 시세 이력 로드 ──────────────────────────────────────
  const loadChart = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    try {
      const res = await fetchStockChart(ticker, period);
      setChartData(res.price_history || []);
    } catch {
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [ticker, period]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const dateList = chartData.map(d => d.date);
  const diffCls  = diff_rate == null ? '' : Number(diff_rate) >= 0 ? 'pos' : 'neg';

  // ── 종목 선택 없이 직접 진입 ─────────────────────────────
  if (!hasState) {
    return (
      <div className="mb-root mb-back-layout">
        <header className="mb-back-header">
          <div className="mb-back-header-row">
            <button className="mb-back-btn" onClick={() => navigate('/mobile/turn-status')}>‹</button>
            <span className="mb-back-header-title">시세 그래프</span>
          </div>
        </header>
        <main className="mb-back-content">
          <div className="mb-empty" style={{ paddingTop: 72 }}>
            차수별현황에서 종목을 선택해주세요.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="mb-root mb-back-layout">

      {/* ── 헤더 ── */}
      <header className="mb-back-header">
        <div className="mb-back-header-row">
          <button className="mb-back-btn" onClick={() => navigate(-1)}>‹</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mb-back-header-title" style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {stock_name || ticker}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, paddingLeft: 8 }}>
            {current_price != null && (
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
                {fPrice(current_price)}
              </div>
            )}
            {diff_rate != null && (
              <div className={diffCls} style={{ fontSize: 11 }}>
                {fRate(diff_rate)}
              </div>
            )}
          </div>
        </div>
        {(account_name || term_type) && (
          <div className="mb-back-header-sub">
            {[account_name, term_type].filter(Boolean).join(' · ')}
          </div>
        )}
      </header>

      {/* ── 스크롤 본문 ── */}
      <main className="mb-back-content">

        {/* 기간 칩 */}
        <div className="sc-period-wrap">
          {PERIODS.map(({ value, label }) => (
            <button
              key={value}
              className={`mb-chip${period === value ? ' active' : ''}`}
              onClick={() => setPeriod(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 차트 카드 */}
        <div className="sc-chart-card">
          {loading ? (
            <div className="mb-loading" style={{ height: 260 }}>불러오는 중...</div>
          ) : chartData.length === 0 ? (
            <div className="mb-empty" style={{ height: 260 }}>시세 데이터가 없습니다.</div>
          ) : (
            <div style={{ paddingTop: 12, paddingBottom: 4, paddingRight: 8 }}>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9, fill: '#aaa' }}
                    interval={PERIOD_INTERVAL[period] - 1}
                    tickFormatter={d => formatTick(d, period)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9, fill: '#aaa' }}
                    width={48}
                    tickFormatter={fAxisPrice}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip turns={unsoldTurns} />}
                    cursor={{ stroke: '#ccc', strokeWidth: 1 }}
                  />

                  {/* 시세 라인 */}
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#4a6cf7"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 3, fill: '#4a6cf7' }}
                  />

                  {/* 차수별 매입단가 수평선 */}
                  {unsoldTurns.map(t => {
                    const color = turnColor(t.turn_no);
                    return (
                      <ReferenceLine
                        key={`h-${t.turn_no}`}
                        y={t.buy_price}
                        stroke={color}
                        strokeDasharray="4 3"
                        strokeWidth={1}
                        label={<HRefLabel color={color} caption={`${t.turn_no}차`} />}
                      />
                    );
                  })}

                  {/* 차수별 매수일 수직선 */}
                  {unsoldTurns.map(t => {
                    const closest = findClosestDate(t.buy_date, dateList);
                    if (!closest) return null;
                    const color = turnColor(t.turn_no);
                    return (
                      <ReferenceLine
                        key={`v-${t.turn_no}`}
                        x={closest}
                        stroke={color}
                        strokeDasharray="4 3"
                        strokeWidth={1}
                        label={<VRefLabel color={color} caption={`${t.turn_no}차`} />}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 범례 */}
          {unsoldTurns.length > 0 && !loading && chartData.length > 0 && (
            <div className="sc-legend">
              {unsoldTurns.map(t => (
                <div key={t.turn_no} className="sc-legend-item">
                  <span className="sc-legend-dot" style={{ background: turnColor(t.turn_no) }} />
                  <span>{t.turn_no}차 {fPrice(t.buy_price)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 차수 요약 테이블 */}
        {unsoldTurns.length > 0 && (
          <div className="sc-summary-card">
            <table className="sc-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>차수</th>
                  <th style={{ textAlign: 'left' }}>매수일</th>
                  <th style={{ textAlign: 'right' }}>매입단가</th>
                  <th style={{ textAlign: 'right' }}>수량</th>
                  <th style={{ textAlign: 'right' }}>수익률</th>
                </tr>
              </thead>
              <tbody>
                {unsoldTurns.map(t => {
                  const color   = turnColor(t.turn_no);
                  const rateCls = t.profit_rate == null ? ''
                    : Number(t.profit_rate) >= 0 ? 'pos' : 'neg';
                  return (
                    <tr key={t.turn_no}>
                      <td style={{ color, fontWeight: 700 }}>{t.turn_no}차</td>
                      <td style={{ color: '#666' }}>{fDate(t.buy_date)}</td>
                      <td style={{ textAlign: 'right' }}>{fPrice(t.buy_price)}</td>
                      <td style={{ textAlign: 'right', color: '#555' }}>
                        {t.quantity != null ? fQty(t.quantity) : '—'}
                      </td>
                      <td className={rateCls} style={{ textAlign: 'right', fontWeight: 600 }}>
                        {t.profit_rate != null ? fRate(t.profit_rate) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </main>
    </div>
  );
}
